import React from 'react'
import SearchProduct from 'client/modules/inventory/pages/purchase/details/components/component/SearchProduct'
import { get, post } from 'client/utils/api-routes.util'
import CloseIcon from 'client/utils/icons/close-icon'
import DownloadIcon from 'client/utils/icons/download-icon'
import SaveIcon from 'client/utils/icons/save-icon'
import ScanBarcodeIcon from 'client/utils/icons/scan-barcode-icon'
import TrashIcon from 'client/utils/icons/trash-icon'
import UploadIcon from 'client/utils/icons/upload-icon'
import _ from 'lodash'
import {
  DcButton,
  DcDropDown,
  DcFlexBox,
  DcInput,
  DcText,
  SectionContent,
  PixelFactoryContext,
  DcOverlaySlider,
  DcDiv,
} from 'pixel-base'
import DcScanner from 'pixel-base/components/dc-scanner'
import * as XLSX from 'xlsx'
import ValidationResultsSlider from './ValidationResultsSlider'
import { IPixelFactory } from '../../../../decl'

// FileUploadDropzone component
interface FileUploadDropzoneProps {
  onFileSelect: (files: File[]) => void
}

const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  onFileSelect,
}) => {
  const [isDragOver, setIsDragOver] = React.useState(false)
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      const file = files[0]
      if (
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        setSelectedFile(file)
        onFileSelect([file])
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) {
      const file = files[0]
      if (
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        setSelectedFile(file)
        onFileSelect([file])
      }
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <DcFlexBox flexDirection="column" gap="10px">
      <div
        style={{
          border: `2px dashed ${isDragOver ? '#007bff' : '#dee2e6'}`,
          borderRadius: '8px',
          padding: '40px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragOver ? '#f8f9fa' : '#ffffff',
          transition: 'all 0.2s ease',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {selectedFile ? (
          <DcFlexBox flexDirection="column" gap="10px" alignItems="center">
            <DcText
              textSize="16px"
              style={{ color: '#28a745', fontWeight: '600' }}
            >
              ✅ File Selected
            </DcText>
            <DcText textSize="14px" style={{ color: '#495057' }}>
              {selectedFile.name}
            </DcText>
            <DcText textSize="12px" style={{ color: '#6c757d' }}>
              Click to select a different file
            </DcText>
          </DcFlexBox>
        ) : (
          <DcFlexBox flexDirection="column" gap="10px" alignItems="center">
            <UploadIcon size={48} color="#6c757d" />
            <DcText
              textSize="16px"
              style={{ fontWeight: '600', color: '#495057' }}
            >
              {isDragOver
                ? 'Drop your file here'
                : 'Drag and drop your file here'}
            </DcText>
            <DcText textSize="14px" style={{ color: '#6c757d' }}>
              or click to browse
            </DcText>
            <DcText textSize="12px" style={{ color: '#6c757d' }}>
              Supports CSV, XLSX, and XLS files
            </DcText>
          </DcFlexBox>
        )}
      </div>
    </DcFlexBox>
  )
}

// Interfaces
interface IWarehouse {
  wh_id: number
  id: number
  name: string
  warehouse_code: string
  is_default: boolean | null
  address: string
  description: string
  city: string
  zip: string | null
  province: string | null
  country: string | null
  phone: string
  active: boolean
  created_at: {
    date: string
    timezone_type: number
    timezone: string
  }
}

interface IProduct {
  product_id: number
  product_name: string
  product_sku?: string
  sku?: string
  product_image_path?: string
  image?: string
  price?: number
  stock_quantity?: number
}

// Marketplace minimal shape used for destination when transfer_type = 'marketplace'
interface IMarketplaceRef {
  id: number
  name: string
}

const marketplaceToWarehouse = (m: IMarketplaceRef): IWarehouse => {
  return {
    wh_id: m.id,
    id: m.id,
    name: m.name,
    warehouse_code: '',
    is_default: false,
    address: '',
    description: '',
    city: '',
    zip: null,
    province: null,
    country: null,
    phone: '',
    active: true,
    created_at: {
      date: new Date().toISOString(),
      timezone_type: 3,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    },
  }
}

interface IInventoryRequestItem {
  id: number | null
  product: IProduct
  quantity: number
  notes?: string
}

interface IInventoryRequestForm {
  source_warehouse_id: IWarehouse | null
  destination_warehouse_id: IWarehouse | null
  request_notes: string
  reference_number: string
  transfer_type: string | null
  items: IInventoryRequestItem[]
}

interface InventoryRequestFormProps {
  onClose: () => void
  onSuccess: () => void
}

// Main Component
const InventoryRequestForm: React.FC<InventoryRequestFormProps> = ({
  onClose,
  onSuccess,
}) => {
  const { showSuccess, showFailure, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)

  // State management
  const [warehouses, setWarehouses] = React.useState<IWarehouse[]>([])
  const [marketplaces, setMarketplaces] = React.useState<
    Array<{ id: number; name: string }>
  >([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState({
    error: '',
    field: '',
  })

  const [formData, setFormData] = React.useState<IInventoryRequestForm>({
    source_warehouse_id: null,
    destination_warehouse_id: null,
    request_notes: '',
    reference_number: '',
    transfer_type: 'warehouse',
    items: [],
  })
  // Scanner and file upload states
  const [showScanner, setShowScanner] = React.useState(false)
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const [scannedSKU, setScannedSKU] = React.useState('')
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = React.useState(0)
  const [uploadResults, setUploadResults] = React.useState<{
    validProducts: IProduct[]
    invalidProducts: any[]
    successCount: number
    errorCount: number
    successRate: number
  } | null>(null)
  const [validationResults, setValidationResults] = React.useState<{
    data: Array<{
      product_id?: number
      product_sku: string
      product_name?: string
      product_image_path?: string
      status: 'success' | 'error'
      message: string
      error_code?: string
      details?: any
    }>
    valid_products: any[]
    _meta: {
      total_skus: number
      found_count: number
      error_count: number
      success_rate: number
    }
  } | null>(null)
  const [isValidated, setIsValidated] = React.useState(false)
  const [showValidationResults, setShowValidationResults] =
    React.useState(false)
  const [showSearchProductModal, setShowSearchProductModal] =
    React.useState(false)
  const [searchValue, setSearchValue] = React.useState('')
  const [searchResults, setSearchResults] = React.useState<IProduct[]>([])
  const [isScanning, setIsScanning] = React.useState(false)

  const scannerRef = React.useRef<HTMLVideoElement>(null)

  // Fetch warehouses
  const fetchWarehouses = () => {
    setLoading(true)
    get('/warehouses').subscribe((res: any) => {
      setWarehouses(res.response.data || [])
      setLoading(false)
    })
  }

  const fetchMarketplaces = () => {
    setLoading(true)
    get('/market-places').subscribe((res: any) => {
      setMarketplaces(res.response.data || [])
      setLoading(false)
    })
  }

  React.useEffect(() => {
    fetchWarehouses()
  }, [])

  // Load marketplaces when transfer_type switches to marketplace
  React.useEffect(() => {
    if (formData.transfer_type === 'marketplace' && marketplaces.length === 0) {
      fetchMarketplaces()
    }
  }, [formData.transfer_type])

  // Scanner functionality
  const startScanner = async () => {
    if (isScanning) return // Prevent multiple simultaneous scans

    try {
      setIsScanning(true)
      // Stop any existing stream first
      if (scannerRef.current && scannerRef.current.srcObject) {
        const existingStream = scannerRef.current.srcObject as MediaStream
        existingStream.getTracks().forEach((track) => track.stop())
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      if (scannerRef.current) {
        scannerRef.current.srcObject = stream
        scannerRef.current.onloadedmetadata = () => {
          startScanning()
        }
      }
    } catch (err) {
      setIsScanning(false)
      showFailure('Unable to access camera')
      console.error('Camera access error:', err)
    }
  }

  const startScanning = () => {
    if (!scannerRef.current) return

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return

    const scanFrame = () => {
      if (!scannerRef.current || !context) return

      const video = scannerRef.current
      const { videoWidth, videoHeight } = video

      if (videoWidth === 0 || videoHeight === 0) {
        requestAnimationFrame(scanFrame)
        return
      }

      canvas.width = videoWidth
      canvas.height = videoHeight

      context.drawImage(video, 0, 0, videoWidth, videoHeight)
      const imageData = context.getImageData(0, 0, videoWidth, videoHeight)

      // Mock barcode scanning - replace with actual implementation
      const mockCode = null

      if (mockCode) {
        setScannedSKU(mockCode)
        setShowScanner(false)
        stopScanner()

        if (mockCode) {
          validateScannedProduct(mockCode)
        }
        return
      }

      requestAnimationFrame(scanFrame)
    }

    scanFrame()
  }

  const stopScanner = () => {
    setIsScanning(false)
    if (scannerRef.current && scannerRef.current.srcObject) {
      const stream = scannerRef.current.srcObject as MediaStream
      stream.getTracks().forEach((track) => track.stop())
    }
  }

  const handleScan = (scannedData: string) => {
    if (!scannedData || scannedData.trim() === '') {
      return
    }

    setScannedSKU(scannedData)
    setShowScanner(false)
    stopScanner()

    // Add a small delay to prevent rapid successive calls
    setTimeout(() => {
      validateScannedProduct(scannedData)
    }, 100)
  }

  const validateScannedProduct = (sku: string) => {
    // Prevent duplicate API calls for the same SKU
    if (!sku || sku.trim() === '') {
      showFailure('Invalid SKU scanned')
      return
    }

    showPageLoader()
    get(`/products/lineitems?search=${sku}`).subscribe(
      (res: any) => {
        hidePageLoader()
        if (res.response && res.response.length > 0) {
          const product = res.response[0]
          addProductToRequest(product)
          showSuccess(`Product "${product.product_name}" added successfully`)
        } else {
          showFailure(`Product with SKU "${sku}" not found`)
        }
      },
      (error) => {
        hidePageLoader()
        showFailure('Error searching for product. Please try again.')
        console.error('Product search error:', error)
      },
    )
  }

  const addProductToRequest = (product: IProduct) => {
    const existingItem = formData.items.find(
      (item) => item.product.product_id === product.product_id,
    )

    if (existingItem) {
      showFailure('Product already added to request')
      return
    }

    const newItem: IInventoryRequestItem = {
      id: null,
      product,
      quantity: 1,
      notes: '',
    }

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }))
    resetValidation()
  }

  // File upload functionality
  const handleFileUploadFromModal = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0]
      if (
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        setUploadedFile(file)
        setShowUploadModal(false)
        processUploadedFile(file)
      } else {
        showFailure('Please upload a CSV or Excel file')
      }
    }
  }

  const processUploadedFile = (file: File) => {
    const isCSV = file.type === 'text/csv'
    const isExcel =
      file.type === 'application/vnd.ms-excel' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

    if (isCSV) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string
          const lines = content.split('\n')
          const products: { product_sku: string; quantity: number }[] = []

          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim()
            if (line) {
              const [product_sku, quantity] = line.split(',')
              if (product_sku && quantity) {
                products.push({
                  product_sku: product_sku.trim(),
                  quantity: parseInt(quantity.trim()) || 1,
                })
              }
            }
          }

          validateProductsFromAPI(products)
        } catch (error) {
          showFailure('Error processing CSV file')
          console.error('CSV processing error:', error)
        }
      }
      reader.readAsText(file)
    } else if (isExcel) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet) as Record<
            string,
            any
          >[]

          if (jsonData.length === 0) {
            showFailure('The uploaded Excel file is empty.')
            return
          }

          const products: { product_sku: string; quantity: number }[] = []

          jsonData.forEach((row: Record<string, any>) => {
            const productSkuKey = Object.keys(row).find(
              (key) =>
                key.toLowerCase() === 'product_sku' ||
                key.toLowerCase() === 'sku' ||
                key.toLowerCase() === 'product sku',
            )

            const quantityKey = Object.keys(row).find(
              (key) =>
                key.toLowerCase() === 'quantity' ||
                key.toLowerCase() === 'stock_count' ||
                key.toLowerCase() === 'stock count',
            )

            if (productSkuKey && quantityKey) {
              const product_sku = String(row[productSkuKey]).trim()
              const quantity = parseInt(String(row[quantityKey])) || 1

              if (product_sku) {
                products.push({
                  product_sku,
                  quantity,
                })
              }
            }
          })

          if (products.length === 0) {
            showFailure(
              'No valid products found in the Excel file. Please ensure the file has the correct column headers.',
            )
            return
          }

          validateProductsFromAPI(products)
        } catch (error) {
          showFailure('Error processing Excel file')
          console.error('Excel processing error:', error)
        }
      }
      reader.readAsArrayBuffer(file)
    } else {
      showFailure('Unsupported file type. Please upload a CSV or Excel file.')
    }
  }

  const validateProductsFromAPI = (
    products: { product_sku: string; quantity: number }[],
  ) => {
    const productSkus = products.map((p) => p.product_sku)

    setUploadProgress(25)
    showPageLoader()

    Promise.all(
      productSkus.map((sku) =>
        get(`/products/lineitems?search=${sku}`).toPromise(),
      ),
    )
      .then((responses) => {
        hidePageLoader()
        setUploadProgress(75)

        const validProducts: IProduct[] = []
        const invalidProducts: any[] = []

        responses.forEach((res: any, index) => {
          const sku = productSkus[index]
          const quantity = products[index].quantity

          if (res.response && res.response.length > 0) {
            const product = res.response[0]
            validProducts.push({
              ...product,
              quantity,
            })
          } else {
            invalidProducts.push({
              sku,
              error: 'Product not found',
            })
          }
        })

        validProducts.forEach((product) => {
          addProductToRequest(product)
        })

        setUploadResults({
          validProducts,
          invalidProducts,
          successCount: validProducts.length,
          errorCount: invalidProducts.length,
          successRate: (validProducts.length / products.length) * 100,
        })

        setUploadProgress(100)

        if (validProducts.length > 0) {
          showSuccess(`Successfully added ${validProducts.length} products`)
        }
        if (invalidProducts.length > 0) {
          showFailure(`${invalidProducts.length} products could not be found`)
        }
      })
      .catch((error) => {
        hidePageLoader()
        showFailure('Error validating products')
        console.error('Product validation error:', error)
      })
  }

  const downloadTemplate = () => {
    const template = [
      ['product_sku', 'quantity'],
      ['SKU001', '1'],
      ['SKU002', '2'],
    ]

    const ws = XLSX.utils.aoa_to_sheet(template)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Template')
    XLSX.writeFile(wb, 'inventory_request_template.xlsx')
  }

  // Product search functionality
  const handleSearch = (query: string) => {
    if (query.length < 2) return

    setLoading(true)
    get(`/products/lineitems?search=${query}`).subscribe((res: any) => {
      setSearchResults(res.response || [])
      setLoading(false)
    })
  }

  const debounceFn = React.useCallback(
    _.debounce((value: string) => {
      if (value.length > 1) {
        handleSearch(value)
      }
    }, 500),
    [],
  )

  const handleQuantityChange = (index: number, quantity: number) => {
    const updatedItems = [...formData.items]
    updatedItems[index].quantity = quantity
    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
    }))
    resetValidation()
  }

  const removeItem = (index: number) => {
    const updatedItems = formData.items.filter((_, i) => i !== index)
    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
    }))
    resetValidation()
  }

  const validateForm = () => {
    // Validation
    if (!formData.source_warehouse_id) {
      setError({
        error: 'Source warehouse is required',
        field: 'source_warehouse_id',
      })
      return false
    }

    if (!formData.destination_warehouse_id) {
      setError({
        error: 'Destination warehouse is required',
        field: 'destination_warehouse_id',
      })
      return false
    }

    if (
      formData.transfer_type !== 'marketplace' &&
      formData.source_warehouse_id.wh_id ===
        formData.destination_warehouse_id.wh_id
    ) {
      setError({
        error: 'Source and destination warehouses cannot be the same',
        field: 'destination_warehouse_id',
      })
      return false
    }

    if (!formData.reference_number.trim()) {
      setError({ error: 'Reference ID is required', field: 'reference_number' })
      return false
    }

    if (formData.items.length === 0) {
      showFailure('Please add at least one product to the request')
      return false
    }

    return true
  }

  const handleValidate = () => {
    if (!validateForm()) return

    // Prepare validation payload
    const validationPayload = {
      product_skus: formData.items
        .map((item) => {
          // Handle different possible SKU field names from API response
          return item.product.product_sku || item.product.sku || ''
        })
        .filter((sku) => sku), // Filter out empty/null values
      warehouse_id: formData.source_warehouse_id!.wh_id,
      inventory_request: formData.items.reduce((acc, item) => {
        const sku = item.product.product_sku || item.product.sku
        if (sku) {
          acc[sku] = item.quantity
        }
        return acc
      }, {} as Record<string, number>),
    }

    // Call validation endpoint
    showPageLoader()
    post('/product/validate-products', validationPayload).subscribe(
      (validationRes: any) => {
        hidePageLoader()

        if (validationRes.response && validationRes.response.data) {
          // Store validation results
          setValidationResults(validationRes.response)

          // Check if all products are valid
          const allValid = validationRes.response.data.every(
            (item: any) => item.status === 'success',
          )

          if (allValid) {
            setIsValidated(true)
            showSuccess(
              'Validation successful! You can now create the request.',
            )
          } else {
            // Some products failed validation, show details
            setShowValidationResults(true)
            showFailure(
              'Some products failed validation. Please review the details.',
            )
          }
        } else {
          // Validation failed
          const errorMessage =
            validationRes.response?.message || 'Product validation failed'
          showFailure(errorMessage)
        }
      },
      (validationError) => {
        hidePageLoader()
        showFailure('Error validating products. Please try again.')
        console.error('Validation error:', validationError)
      },
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!isValidated) {
      showFailure('Please validate the request first before creating it.')
      return
    }

    // Create the request
    const requestData = {
      source_warehouse_id: formData.source_warehouse_id!.wh_id,
      destination_warehouse_id: formData.destination_warehouse_id!.wh_id,
      request_notes: formData.request_notes,
      reference_number: formData.reference_number,
      transfer_type: formData.transfer_type || 'warehouse',
      products: formData.items.map((item) => ({
        product_id: item.product.product_id,
        quantity: item.quantity,
        notes: item.notes || '',
      })),
    }

    showPageLoader()
    post('/warehouse/transfer', requestData).subscribe((res: any) => {
      hidePageLoader()
      if (
        res.response.message ==
        'Inventory transfer request created successfully'
      ) {
        showSuccess('Inventory request created successfully')
        // Reset form and validation state
        setFormData({
          source_warehouse_id: null,
          destination_warehouse_id: null,
          request_notes: '',
          reference_number: '',
          items: [],
          transfer_type: 'warehouse',
        })
        setIsValidated(false)
        setValidationResults(null)
        onSuccess()
        onClose()
      } else {
        showFailure(res.response.error || 'Failed to create inventory request')
      }
    })
  }

  const clearError = (field: string) => {
    if (error.field === field) {
      setError({ error: '', field: '' })
    }
  }

  const resetValidation = () => {
    if (isValidated) {
      setIsValidated(false)
      setValidationResults(null)
      setShowValidationResults(false)
    }
  }

  const totalItems = () => {
    return formData.items.reduce((acc, item) => acc + item.quantity, 0)
  }

  const totalAmount = () => {
    return formData.items.reduce((acc, item) => {
      const price = item.product.price || 0
      return acc + price * item.quantity
    }, 0)
  }

  return (
    <DcFlexBox gap="20px" flexDirection="column">
      <DcFlexBox height="fit-content" justifyContent="flex-end" gap="5px">
        <DcButton
          variant="outline-success"
          onClick={() => {
            if (!isScanning) {
              setShowScanner(true)
              setScannedSKU('')
              setTimeout(() => startScanner(), 100)
            }
          }}
          withIcon={true}
          size="sm"
          disabled={isScanning}
        >
          <ScanBarcodeIcon size={16} />
          <span style={{ fontSize: '12px' }}>
            {isScanning ? 'Scanning...' : 'Scan'}
          </span>
        </DcButton>
        <DcButton
          variant="outline-info"
          onClick={() => setShowUploadModal(true)}
          withIcon={true}
          size="sm"
        >
          <UploadIcon size={16} />
          <span style={{ fontSize: '12px' }}>Upload</span>
        </DcButton>
      </DcFlexBox>
      {/* Left Panel - Product Selection */}
      <DcFlexBox height="auto" gap="10px">
        <SectionContent
          style={{
            gap: '15px',
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            width: '65%',
          }}
        >
          {/* Product Search */}
          <DcFlexBox
            height="auto"
            gap="10px"
            style={{
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '15px',
              border: '1px solid #e9ecef',
            }}
          >
            <DcFlexBox style={{ flex: 1, gap: '10px' }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  setShowSearchProductModal(true)
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                }}
              >
                <DcInput
                  name="search"
                  showsearchicon={1}
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value)
                    debounceFn(e.target.value)
                  }}
                  placeholder={'Search Product by SKU or Name'}
                  style={{ flex: 1 }}
                />
                <DcButton
                  variant="outline-primary"
                  onClick={() => {
                    setShowSearchProductModal(true)
                  }}
                >
                  Browse
                </DcButton>
              </form>
            </DcFlexBox>

            {/* Scanner and File Upload Buttons */}
          </DcFlexBox>

          {/* Selected Products List */}
          {formData.items.length > 0 && (
            <DcFlexBox height="auto" flexDirection="column" gap="8px">
              <DcFlexBox
                height="auto"
                justifyContent="space-between"
                alignItems="center"
              >
                <DcText
                  fontSize=".875rem"
                  style={{
                    fontWeight: '600',
                    color: '#495057',
                  }}
                >
                  Selected Products ({formData.items.length})
                </DcText>
                <DcText fontSize="12px" style={{ color: '#6c757d' }}>
                  Total Items: {totalItems()}
                </DcText>
              </DcFlexBox>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {formData.items.map((item, index) => (
                  <DcFlexBox
                    height="auto"
                    key={index}
                    justifyContent="space-between"
                    alignItems="center"
                    style={{
                      padding: '12px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      background: '#ffffff',
                    }}
                  >
                    <DcFlexBox
                      height="auto"
                      flexDirection="row"
                      gap="4px"
                      style={{ flex: 1 }}
                      width="80%"
                    >
                      {item.product.image ? (
                        <img
                          src={item.product.image}
                          alt={item.product.product_name}
                          style={{
                            width: 50,
                            height: 50,
                            borderRadius: 4,
                            objectFit: 'cover',
                            marginBottom: 4,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 50,
                            height: 50,
                            borderRadius: 4,
                            background: '#f0f0f0',
                            marginBottom: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#bbb',
                            fontSize: 12,
                          }}
                        >
                          No Image
                        </div>
                      )}
                      <DcFlexBox height="auto" flexDirection="column" gap="4px">
                        <DcText fontSize="14px" style={{ fontWeight: '500' }}>
                          {item.product.product_name}
                        </DcText>
                        <DcText fontSize="12px" style={{ color: '#6c757d' }}>
                          SKU: {item.product?.sku}
                        </DcText>
                      </DcFlexBox>
                    </DcFlexBox>
                    <DcFlexBox
                      height="auto"
                      alignItems="center"
                      gap="8px"
                      width="20%"
                      style={{ marginRight: '10px' }}
                    >
                      <DcDiv width="100px">
                        <DcInput
                          inputSize="small"
                          name="quantity"
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(
                              index,
                              parseInt(e.target.value) || 1,
                            )
                          }
                          style={{
                            width: '100px',
                            height: '32px',
                            fontSize: '12px',
                          }}
                        />
                      </DcDiv>
                      <DcButton
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeItem(index)}
                      >
                        <TrashIcon size={14} />
                      </DcButton>
                    </DcFlexBox>
                  </DcFlexBox>
                ))}
              </div>
            </DcFlexBox>
          )}
        </SectionContent>

        {/* Right Panel - Form */}
        <form style={{ width: '35%' }} onSubmit={handleSubmit}>
          <SectionContent
            style={{
              gap: '15px',
              display: 'flex',
              flexDirection: 'column',
              background: '#ffffff',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
          >
            {/* Reference Number */}
            <DcFlexBox height="auto" flexDirection="column" gap="8px">
              <DcText
                fontSize=".875rem"
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                }}
              >
                Reference Number *
              </DcText>
              <DcInput
                name="reference_number"
                error={
                  error.field === 'reference_number' && error.error !== ''
                    ? error.error
                    : undefined
                }
                required
                type={'text'}
                placeholder={'Enter Reference Number'}
                value={formData.reference_number}
                onChange={(e) => {
                  clearError('reference_number')
                  resetValidation()
                  setFormData({
                    ...formData,
                    reference_number: e.target.value,
                  })
                }}
              />
            </DcFlexBox>

            {/* Source Warehouse Selection */}

            <DcFlexBox height="auto" flexDirection="column" gap="8px">
              <DcText
                fontSize=".875rem"
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                }}
              >
                Source Warehouse *
              </DcText>
              <DcDropDown
                error={
                  error.field === 'source_warehouse_id' && error.error !== ''
                    ? error.error
                    : undefined
                }
                customLabel="Source Warehouse"
                required
                placeholder="Select Source Warehouse"
                options={[
                  {
                    name: 'Select Source Warehouse',
                    wh_id: 0,
                  },
                  ...warehouses,
                ].map((data) => {
                  return {
                    label: data.name,
                    value: data.wh_id.toString(),
                  }
                })}
                value={formData.source_warehouse_id?.wh_id?.toString() || ''}
                onChange={(e) => {
                  clearError('source_warehouse_id')
                  resetValidation()
                  const selectedWarehouse = warehouses.find(
                    (w) => w.wh_id.toString() === e.target.value,
                  )
                  setFormData({
                    ...formData,
                    source_warehouse_id: selectedWarehouse || null,
                  })
                }}
              />
            </DcFlexBox>
            <DcFlexBox height="auto" flexDirection="column" gap="8px">
              <DcText
                fontSize=".875rem"
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                }}
              >
                Transfer To *
              </DcText>
              <DcDropDown
                error={
                  error.field === 'source_warehouse_id' && error.error !== ''
                    ? error.error
                    : undefined
                }
                customLabel="Transfer Type"
                required
                placeholder="Select Transfer Type"
                options={[
                  {
                    name: 'Select Transfer Type',
                    wh_id: '',
                  },
                  {
                    name: 'Warehouse',
                    wh_id: 'warehouse',
                  },
                  {
                    name: ' Marketplace',
                    wh_id: 'marketplace',
                  },
                ].map((data) => {
                  return {
                    label: data.name,
                    value: data.wh_id.toString(),
                  }
                })}
                value={formData.transfer_type || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    transfer_type: e.target.value || '',
                  })
                }}
              />
              {/* <DcFlexBox height="auto" flexDirection="row" gap="8px">
                <DcButton
                  width="50%"
                  variant={
                    formData.transfer_type === 'warehouse'
                      ? 'primary'
                      : 'outline-primary'
                  }
                  onClick={() => {
                    setFormData({
                      ...formData,
                      transfer_type: 'warehouse',
                    })
                  }}
                >
                  Warehouse
                </DcButton>
                <DcButton
                  width="50%"
                  variant={
                    formData.transfer_type === 'marketplace'
                      ? 'primary'
                      : 'outline-primary'
                  }
                  onClick={() => {
                    setFormData({
                      ...formData,
                      transfer_type: 'marketplace',
                    })
                  }}
                >
                  Market Place
                </DcButton>
              </DcFlexBox> */}
            </DcFlexBox>
            {/* Destination Warehouse Selection */}
            <DcFlexBox height="auto" flexDirection="column" gap="8px">
              <DcText
                fontSize=".875rem"
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                }}
              >
                {formData.transfer_type === 'marketplace'
                  ? 'Destination Marketplace *'
                  : 'Destination Warehouse *'}
              </DcText>
              <DcDropDown
                error={
                  error.field === 'destination_warehouse_id' &&
                  error.error !== ''
                    ? error.error
                    : undefined
                }
                customLabel={
                  formData.transfer_type === 'marketplace'
                    ? 'Destination Marketplace'
                    : 'Destination Warehouse'
                }
                required
                placeholder={
                  formData.transfer_type === 'marketplace'
                    ? 'Select Destination Marketplace'
                    : 'Select Destination Warehouse'
                }
                options={
                  formData.transfer_type === 'marketplace'
                    ? (
                        [
                          { name: 'Select Destination Marketplace', id: 0 },
                          ...marketplaces,
                        ] as Array<{ name: string; id: number }>
                      ).map((data) => ({
                        label: data.name,
                        value: data.id.toString(),
                      }))
                    : (
                        [
                          { name: 'Select Destination Warehouse', wh_id: 0 },
                          ...warehouses.filter(
                            (w) =>
                              w.wh_id !== formData.source_warehouse_id?.wh_id,
                          ),
                        ] as Array<{ name: string; wh_id: number }>
                      ).map((data) => ({
                        label: data.name,
                        value: data.wh_id.toString(),
                      }))
                }
                value={
                  formData.destination_warehouse_id?.wh_id?.toString() || ''
                }
                onChange={(e) => {
                  clearError('destination_warehouse_id')
                  resetValidation()
                  if (formData.transfer_type === 'marketplace') {
                    const selectedMarketplace = marketplaces.find(
                      (m) => m.id.toString() === e.target.value,
                    )
                    // Store as a warehouse-like object to preserve downstream usage (wh_id)
                    const destinationLike: IWarehouse | null =
                      selectedMarketplace
                        ? marketplaceToWarehouse({
                            id: selectedMarketplace.id,
                            name: selectedMarketplace.name,
                          })
                        : null
                    setFormData({
                      ...formData,
                      destination_warehouse_id: destinationLike,
                    })
                  } else {
                    const selectedWarehouse = warehouses.find(
                      (w) => w.wh_id.toString() === e.target.value,
                    )
                    setFormData({
                      ...formData,
                      destination_warehouse_id: selectedWarehouse || null,
                    })
                  }
                }}
              />
            </DcFlexBox>

            {/* Notes */}
            <DcFlexBox height="auto" flexDirection="column" gap="8px">
              <DcText
                fontSize=".875rem"
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                }}
              >
                Notes
              </DcText>
              <DcInput
                name="request_notes"
                as="textarea"
                error={
                  error.field === 'request_notes' && error.error !== ''
                    ? error.error
                    : undefined
                }
                type={'text'}
                placeholder={'Add notes about this inventory request...'}
                value={formData.request_notes}
                onChange={(e) => {
                  clearError('request_notes')
                  resetValidation()
                  setFormData({
                    ...formData,
                    request_notes: e.target.value,
                  })
                }}
                style={{ minHeight: '80px' }}
              />
            </DcFlexBox>

            {/* Validation Status */}
            {isValidated && (
              <DcFlexBox
                style={{
                  background: '#d4edda',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #c3e6cb',
                  marginBottom: '10px',
                }}
              >
                <DcText
                  textSize="14px"
                  style={{ color: '#155724', fontWeight: '500' }}
                >
                  ✅ Validation successful! You can now create the request.
                </DcText>
              </DcFlexBox>
            )}

            {/* Action Buttons */}
            <DcFlexBox
              height="auto"
              justifyContent="flex-end"
              gap="10px"
              style={{ marginTop: '20px' }}
            >
              <DcButton
                variant="outline-secondary"
                onClick={onClose}
                disabled={loading}
                withIcon={true}
              >
                <CloseIcon size={16} />
                <span>Cancel</span>
              </DcButton>
              {!isValidated ? (
                <DcButton
                  onClick={handleValidate}
                  disabled={loading || formData.items.length === 0}
                  withIcon={true}
                >
                  <SaveIcon size={16} />
                  <span>Validate Request</span>
                </DcButton>
              ) : (
                <DcButton type="submit" disabled={loading} withIcon={true}>
                  <SaveIcon size={16} />
                  <span>Create Request</span>
                </DcButton>
              )}
            </DcFlexBox>
          </SectionContent>
        </form>
      </DcFlexBox>

      {/* Scanner Modal */}
      <DcScanner
        isOpen={showScanner}
        onClose={() => {
          setShowScanner(false)
          stopScanner()
        }}
        scannedSKU={scannedSKU}
        setScannedSKU={setScannedSKU}
        handleScan={handleScan}
        scannerRef={scannerRef}
      />

      {/* File Upload Modal */}
      <DcOverlaySlider
        title={'Upload Products from File'}
        width="50%"
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      >
        <DcFlexBox flexDirection="column" gap="20px">
          <DcText textSize="14px" style={{ color: '#6c757d' }}>
            Upload a CSV or Excel file with the following columns: SKU, Quantity
          </DcText>

          {/* Template Download Section */}
          <DcFlexBox
            style={{
              background: '#e3f2fd',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #bbdefb',
              height: 'auto',
            }}
          >
            <DcFlexBox flexDirection="column" gap="8px" style={{ flex: 1 }}>
              <DcText
                textSize="14px"
                style={{ fontWeight: '600', color: '#1976d2' }}
              >
                📋 Download Template
              </DcText>
              <DcText textSize="12px" style={{ color: '#1976d2' }}>
                Use our template to ensure your file has the correct format
              </DcText>
            </DcFlexBox>
            <DcButton
              size="sm"
              variant="outline-primary"
              onClick={downloadTemplate}
              withIcon={true}
            >
              <DownloadIcon size={16} />
              Download Template
            </DcButton>
          </DcFlexBox>

          {/* Drag and Drop Area */}
          <DcFlexBox flexDirection="column" gap="10px">
            <DcText
              textSize="14px"
              style={{ fontWeight: '600', color: '#495057' }}
            >
              Upload File
            </DcText>
            <FileUploadDropzone onFileSelect={handleFileUploadFromModal} />
          </DcFlexBox>

          {/* File Format Info */}
          <DcFlexBox
            style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #dee2e6',
            }}
          >
            <DcFlexBox flexDirection="column" gap="4px">
              <DcText
                textSize="12px"
                style={{ fontWeight: '600', color: '#495057' }}
              >
                📄 Supported File Formats:
              </DcText>
              <DcText textSize="11px" style={{ color: '#6c757d' }}>
                • CSV files (.csv)
              </DcText>
              <DcText textSize="11px" style={{ color: '#6c757d' }}>
                • Excel files (.xlsx, .xls)
              </DcText>
              <DcText textSize="11px" style={{ color: '#6c757d' }}>
                • First row should contain headers: product_sku, quantity
              </DcText>
            </DcFlexBox>
          </DcFlexBox>
        </DcFlexBox>
      </DcOverlaySlider>

      {/* Upload Results Modal */}
      {uploadResults && (
        <DcOverlaySlider
          title={'Upload Results'}
          width="40%"
          isOpen={!!uploadResults}
          onClose={() => setUploadResults(null)}
        >
          <DcFlexBox flexDirection="column" gap="16px">
            <DcFlexBox
              style={{
                background: '#f0f9ff',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #0ea5e9',
              }}
            >
              <DcText textSize="14px" style={{ color: '#0c4a6e' }}>
                📊 Upload Summary
              </DcText>
              <DcFlexBox gap="16px" style={{ marginTop: '8px' }}>
                <DcFlexBox flexDirection="column" alignItems="center">
                  <DcText
                    textSize="16px"
                    style={{ fontWeight: '600', color: '#059669' }}
                  >
                    {uploadResults.successCount}
                  </DcText>
                  <DcText textSize="12px" style={{ color: '#059669' }}>
                    Success
                  </DcText>
                </DcFlexBox>
                <DcFlexBox flexDirection="column" alignItems="center">
                  <DcText
                    textSize="16px"
                    style={{ fontWeight: '600', color: '#dc2626' }}
                  >
                    {uploadResults.errorCount}
                  </DcText>
                  <DcText textSize="12px" style={{ color: '#dc2626' }}>
                    Errors
                  </DcText>
                </DcFlexBox>
                <DcFlexBox flexDirection="column" alignItems="center">
                  <DcText
                    textSize="16px"
                    style={{ fontWeight: '600', color: '#1d4ed8' }}
                  >
                    {uploadResults.successRate.toFixed(1)}%
                  </DcText>
                  <DcText textSize="12px" style={{ color: '#1d4ed8' }}>
                    Success Rate
                  </DcText>
                </DcFlexBox>
              </DcFlexBox>
            </DcFlexBox>

            {uploadResults.invalidProducts.length > 0 && (
              <DcFlexBox flexDirection="column" gap="8px">
                <DcText
                  textSize="14px"
                  style={{ fontWeight: '600', color: '#dc2626' }}
                >
                  ❌ Invalid Products ({uploadResults.invalidProducts.length})
                </DcText>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {uploadResults.invalidProducts.map((item, index) => (
                    <DcFlexBox
                      key={index}
                      style={{
                        padding: '8px 12px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '4px',
                        marginBottom: '4px',
                      }}
                    >
                      <DcText textSize="12px" style={{ color: '#dc2626' }}>
                        SKU: {item.sku} - {item.error}
                      </DcText>
                    </DcFlexBox>
                  ))}
                </div>
              </DcFlexBox>
            )}
          </DcFlexBox>
        </DcOverlaySlider>
      )}

      {/* Product Search Modal */}
      <DcOverlaySlider
        isOpen={showSearchProductModal}
        onClose={() => {
          setShowSearchProductModal(false)
        }}
        title="Search Product"
        width="70%"
      >
        <SectionContent style={{ padding: '0px' }}>
          <SearchProduct
            searchValue={searchValue}
            handleClose={(selectedItems) => {
              if (Array.isArray(selectedItems)) {
                selectedItems.forEach((product) => {
                  addProductToRequest(product)
                })
              } else {
                addProductToRequest(selectedItems)
              }
              setShowSearchProductModal(false)
            }}
            categories={[]}
            cart={formData.items.map((item) => item.product)}
          />
        </SectionContent>
      </DcOverlaySlider>

      {/* Validation Results Slider */}
      <ValidationResultsSlider
        isOpen={showValidationResults}
        onClose={() => {
          setShowValidationResults(false)
          setValidationResults(null)
        }}
        validationResults={validationResults}
        onRemoveFailedProducts={() => {
          if (validationResults) {
            setShowValidationResults(false)
            setValidationResults(null)
            // Clear the form items that failed validation
            const failedSkus = validationResults.data
              .filter((item) => item.status === 'error')
              .map((item) => item.product_sku)

            setFormData((prev) => ({
              ...prev,
              items: prev.items.filter(
                (item) =>
                  !failedSkus.includes(
                    item.product.product_sku || item.product.sku || '',
                  ),
              ),
            }))
            showSuccess('Failed products removed from request')
          }
        }}
      />
    </DcFlexBox>
  )
}

export default InventoryRequestForm
